import type { Player as IVSPlayer, PlayerEventType as IVSEvents, TextMetadataCue } from 'amazon-ivs-player';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Slide } from '../datasource/generated';
import { useGetSlidesByItemSkQuery } from '../datasource/queries/slides';
import { useAppSelector } from '.';

const IvsMetadataEventType = {
    Slide: 'slide',
    LiveCaption: 'live-caption',
    LiveCaptionHeartbeat: 'live-caption-heartbeat',
} as const;

const LiveCaptionsFallbackSourceVideoDelaySeconds = 8;

type SlideMetadataEvent = {
    type: typeof IvsMetadataEventType.Slide;
    slideNumber: number;
    isTitle?: boolean;
    url?: string | null;
    timestampUtcMs?: number;
};

export type LiveCaptionMetadataEvent = {
    type: typeof IvsMetadataEventType.LiveCaption;
    isFinal: boolean;
    text: string;
    timestampUtcMs?: number;
};

export type LiveCaptionHeartbeatMetadataEvent = {
    type: typeof IvsMetadataEventType.LiveCaptionHeartbeat;
};

export type LiveCaptionsMetadataEvent =
    | LiveCaptionMetadataEvent
    | LiveCaptionHeartbeatMetadataEvent;

export type LiveCaptionsFailure = {
    stage: 'initialization' | 'ivs_delivery' | 'render' | 'synchronization';
    category: string;
};

type IvsMetadataEvent = SlideMetadataEvent | LiveCaptionsMetadataEvent;

type ProviderPlayer = {
    ivsPlayer: IVSPlayer;
    ivsEvents: { PlayerEventType: typeof IVSEvents };
};

interface UseSlidesProps {
    onLiveCaptionsMetadata?: (
        metadata: LiveCaptionsMetadataEvent,
        liveLatencySeconds?: number,
    ) => void;
    liveCaptionsAllowed?: boolean;
    onLiveCaptionsFailure?: (failure: LiveCaptionsFailure) => void;
}

export const useSlides = ({
    onLiveCaptionsMetadata,
    onLiveCaptionsFailure,
    liveCaptionsAllowed = false,
}: UseSlidesProps = {}) => {
    const ivsPlayer = useRef<IVSPlayer>();
    const latestSlideTimestampUtcMs = useRef<number>();
    const liveCaptionsAllowedRef = useRef(liveCaptionsAllowed);
    liveCaptionsAllowedRef.current = liveCaptionsAllowed;

    const currentSegmentItemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const currentSegmentPosition = useAppSelector((state) => state.player.playbackProgress.find(x => x.itemSk === currentSegmentItemSk))?.position ?? 0;

    const launchDetails = useAppSelector((state) => state.player.launchDetails);

    const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);

    const {
        data: slides,
        refetch
    } = useGetSlidesByItemSkQuery(
        {
            itemSk: launchDetails?.itemSk ?? 0,
            current: launchDetails?.isWebcast ?? false,
        }, { skip: !launchDetails?.itemSk });

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && launchDetails?.isWebcast) {
                refetch();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [launchDetails?.isWebcast, refetch]);

    useEffect(() => {
        if (launchDetails?.isWebcast && slides && slides.length > 0)
            setCurrentSlide(slides[0]);
    }, [launchDetails?.isWebcast, slides]);

    useEffect(() => {
        if (!launchDetails?.isWebcast)
            setCurrentSlide(null);
    }, [currentSegmentItemSk, launchDetails?.isWebcast]);

    const currentSegmentSlides = useMemo(() => {
        const isSingleSegment = !launchDetails?.segments || launchDetails?.segments.length === 1;

        if (isSingleSegment)
            return slides;

        return slides?.filter((slide) => slide.itemSk === currentSegmentItemSk);

    }, [currentSegmentItemSk, launchDetails?.segments, slides]);


    useEffect(() => {
        if (currentSegmentSlides && !launchDetails?.isWebcast) {

            const ceilPosition = Math.ceil(currentSegmentPosition);

            let foundSlide = null;
            for (const slide of currentSegmentSlides) {
                if ((slide?.position ?? 0) < ceilPosition) {
                    if (!foundSlide || ceilPosition > (foundSlide?.position ?? 0)) {
                        foundSlide = slide;
                    }
                }
            }

            if (foundSlide) {
                if (currentSlide?.syncEventId !== foundSlide.syncEventId) {
                    setCurrentSlide(foundSlide);
                }
            } else {
                setCurrentSlide(null);
            }
        }
    }, [currentSegmentPosition, currentSegmentSlides, currentSlide, launchDetails?.isWebcast]);

    const getLiveLatencySeconds = () => {
        if (typeof ivsPlayer.current?.getLiveLatency !== 'function') {
            return LiveCaptionsFallbackSourceVideoDelaySeconds;
        }

        try {
            const liveLatencySeconds = ivsPlayer.current.getLiveLatency();
            return Number.isFinite(liveLatencySeconds) && liveLatencySeconds >= 0
                ? liveLatencySeconds
                : LiveCaptionsFallbackSourceVideoDelaySeconds;
        } catch {
            return LiveCaptionsFallbackSourceVideoDelaySeconds;
        }
    };

    const handleProviderPlayer = (player: ProviderPlayer) => {
        if (
            !player?.ivsPlayer
            || typeof player.ivsPlayer.addEventListener !== 'function'
            || !player.ivsEvents?.PlayerEventType?.TEXT_METADATA_CUE
        ) {
            onLiveCaptionsFailure?.({
                stage: 'initialization',
                category: 'ivs_metadata_listener_unavailable',
            });
            return;
        }

        ivsPlayer.current = player.ivsPlayer;
        const ivsEvents = player.ivsEvents as { PlayerEventType: typeof IVSEvents };

        try {
            ivsPlayer.current.addEventListener(
                ivsEvents.PlayerEventType.TEXT_METADATA_CUE,
                function (cue: TextMetadataCue) {
                    try {
                        const metadata = JSON.parse(cue.text) as IvsMetadataEvent | null;

                        if (!metadata || typeof metadata !== 'object') return;

                        if ('type' in metadata) {
                            switch (metadata.type) {
                                case IvsMetadataEventType.Slide:
                                    if (
                                        typeof metadata.timestampUtcMs !== 'number'
                                    || !Number.isFinite(metadata.timestampUtcMs)
                                    || (
                                        latestSlideTimestampUtcMs.current !== undefined
                                        && metadata.timestampUtcMs <= latestSlideTimestampUtcMs.current
                                    )
                                    ) {
                                        return;
                                    }

                                    latestSlideTimestampUtcMs.current = metadata.timestampUtcMs;

                                    if (metadata.isTitle || metadata.slideNumber <= 0) {
                                        setCurrentSlide(null);
                                        return;
                                    }

                                    if (metadata.url) {
                                        setCurrentSlide({
                                            slideNumber: metadata.slideNumber,
                                            url: metadata.url,
                                        });
                                    }
                                    return;

                                case IvsMetadataEventType.LiveCaption: {
                                    if (!liveCaptionsAllowedRef.current) return;
                                    if (typeof metadata.text !== 'string') {
                                        onLiveCaptionsFailure?.({
                                            stage: 'ivs_delivery',
                                            category: 'invalid_caption_metadata',
                                        });
                                        return;
                                    }

                                    onLiveCaptionsMetadata?.(
                                        metadata,
                                        getLiveLatencySeconds(),
                                    );
                                    return;
                                }

                                case IvsMetadataEventType.LiveCaptionHeartbeat:
                                    if (!liveCaptionsAllowedRef.current) return;
                                    onLiveCaptionsMetadata?.(metadata);
                                    return;
                            }
                        }
                    } catch {
                        return;
                    }
                }
            );
        } catch {
            onLiveCaptionsFailure?.({
                stage: 'initialization',
                category: 'ivs_metadata_listener_init_failed',
            });
        }
    };

    return {
        ivsPlayer,
        currentSlide,
        handleProviderPlayer,
        currentSegmentSlides,
        slides,
    };
};
